/**
 * Gaat dit artikel met ons eigen transport (de vrachtwagen van glas op maat) in plaats van met de
 * pakketdienst?
 *
 * Aanleiding (13-09-2026): een Morso-verstekstans van tientallen kilo's rekende op de site het
 * pakkettarief en de pakket-levertijd, terwijl zo'n machine nooit met de post meegaat. Hetzelfde
 * geldt voor vouwwanden, balustradeprofielen van 5 m en ladders. De Timmers-API kent geen gewicht
 * of afmetingen, dus dit is een admin-keuze in Productbeheer — per artikel, of in één keer voor
 * een hele groep of subgroep.
 *
 * Wat eigen transport betekent, overal hetzelfde:
 *  - verzendkosten = het glastransport-tarief (resolveShippingCents met hasGlass), in winkelwagen,
 *    checkout, createOrder én de verzendkosten die Google in JSON-LD/feed ziet;
 *  - levertijd = de beheerde "artikel met eigen transport"-tekst;
 *  - de order krijgt shippingMethod "glass_transport" (dus ook de eigen-transportroute).
 *
 * **Lengtes gaan NIET met eigen transport (24-09-2026).** Glaslatten stonden hier automatisch op,
 * op de aanname dat een bos van 5,5 m wel met de vrachtwagen mee moest. Timmers heeft op verzoek
 * van Taco hun eigen indeling gegeven en die kent maar twee categorieën:
 *   - Glastransport € 35,35 — glasleveringen (code 590401 op de orderbevestiging);
 *   - Pakket € 13,10 NL / € 17,70 BE — "pakketten **en lengtes**".
 * Lengtes vallen dus onder pakket. Daarmee verviel niet alleen de glaslat-uitzondering hier, maar
 * ook de 27 balustradeprofielen en afdekkappen van 2500/5000 mm die in de admin waren aangevinkt —
 * terwijl 54 andere lengte-artikelen (U-profielen, afdichtprofielen) al gewoon als pakket gingen.
 * Onze klanttarieven zijn exact deze bedragen, dus een verkeerde vlag kostte de klant € 22,25.
 *
 * **Ook de Morso-machines gaan op het pakkettarief (18-10-2026).** Ze stonden hier even op eigen
 * transport — tientallen kilo's gaan immers niet met de post. Timmers heeft dat op verzoek van
 * Taco bevestigd én weersproken: de machine gaat wél met hun transporteur mee, maar ze rekenen er
 * het PAKKETtarief voor. Hun eigen woorden: "€ 13,10 pakketten, zowel voor eigen transport als
 * Transmission". Het glastransport-tarief van € 35,35 is uitsluitend voor glasleveringen
 * (code 590401). Onze klanttarieven zijn exact hun drie tarieven, dus de vlag aanzetten liet de
 * klant € 22,25 te veel betalen (€ 17,65 in België).
 *
 * Daarmee staat de lijst in Productbeheer leeg: er is op dit moment géén artikel dat eigen
 * transport hoort te volgen. De schakelaar blijft bestaan voor als dat ooit verandert — maar de
 * regel om te onthouden is: het tarief hangt aan WAT het is (glas of niet), niet aan hoe zwaar
 * het is of met welke wagen het meegaat.
 *
 * **Behalve boven 2500 mm (26-10-2026).** Timmers' nieuwe tarieven per 1-11-2026 trekken de grens
 * niet meer bij glas-of-geen-glas, maar bij de lengte: tot 2500 mm een verzendtarief (€ 15 NL /
 * € 20 BE), daarboven glastransport (€ 35,35 NL / € 42,50 BE). "Lengtes vallen onder pakket" uit
 * het blok hierboven geldt dus alleen nog tot 2500 mm. Een U-profiel van zes meter rekende bij ons
 * € 15 terwijl de inkoop € 35,35 kostte — en het viel alleen op als iemand eraan dacht het vinkje
 * in Productbeheer aan te zetten. Daarom is de lengte nu een regel in de code en geen keuze meer:
 * `PAKKET_MAX_LENGTE_MM` gaat vóór het pakket-vinkje. Bij een op-lengte-artikel telt de lengte die
 * de klant koos, niet de handelslengte van de staaf waar hij uit komt.
 */
import type { CatalogOverrides } from "../pricing/overrides";

/** Sleutel voor een hele Timmers-groep in `CatalogOverrides.eigenTransport`. */
export const eigenTransportGroep = (groep: string): string => `groep:${groep}`;
/** Sleutel voor een Timmers-subgroep in `CatalogOverrides.eigenTransport`. */
export const eigenTransportSubgroep = (subgroep: string): string => `subgroep:${subgroep}`;

export interface ArtikelVoorTransport {
  slug: string;
  groep?: string;
  subgroep?: string;
  /** Vaste handelslengte in mm (een staaf van 6000 mm), als die er is. */
  handelslengteMm?: number;
}

/**
 * Boven deze lengte gaat het niet meer als pakket. Timmers' tarieven lopen per 1-11-2026 langs
 * precies deze grens: tot 2500 mm een verzendtarief (€ 15 NL / € 20 BE), daarboven glastransport
 * (€ 35,35 NL / € 42,50 BE).
 */
export const PAKKET_MAX_LENGTE_MM = 2500;

/**
 * Sleutel voor "dit artikel gaat tóch als pakket" — de keuze Pakket in de dropdown per artikel,
 * die een aangevinkte groep of subgroep overstemt (bv. de losse messen in de Morso-groep).
 */
export const pakketArtikel = (slug: string): string => `pakket:${slug}`;

/** De keuze per artikel in Productbeheer: volgen van de groep, of expliciet pakket / eigen transport. */
export type TransportKeuze = "auto" | "pakket" | "eigen";

export function transportKeuze(slug: string, ov: Pick<CatalogOverrides, "eigenTransport"> | null | undefined): TransportKeuze {
  const lijst = ov?.eigenTransport ?? [];
  if (lijst.includes(slug)) return "eigen";
  if (lijst.includes(pakketArtikel(slug))) return "pakket";
  return "auto";
}

/** Zet de keuze voor één artikel; geeft de nieuwe lijst terug (groep-/subgroep-sleutels blijven staan). */
export function metTransportKeuze(lijst: readonly string[] | undefined, slug: string, keuze: TransportKeuze): string[] {
  const rest = (lijst ?? []).filter((k) => k !== slug && k !== pakketArtikel(slug));
  if (keuze === "eigen") rest.push(slug);
  if (keuze === "pakket") rest.push(pakketArtikel(slug));
  return rest;
}

/** Waaróm gaat dit artikel met eigen transport — of null als het gewoon een pakket is. */
export function eigenTransportReden(
  art: ArtikelVoorTransport | null | undefined,
  ov: Pick<CatalogOverrides, "eigenTransport"> | null | undefined,
  /** De lengte die de klant koos (mm), bij een op-lengte-artikel. Overstemt de handelslengte. */
  gekozenLengteMm?: number | null,
): "artikel" | "groep" | "subgroep" | "lengte" | null {
  if (!art) return null;
  // Te lang voor de pakketdienst. Dit staat vóór het pakket-vinkje: hoe je een profiel van zes
  // meter ook instelt, hij past niet in een pakket en Timmers rekent er glastransport voor.
  // Zonder deze regel rekenden we € 15 terwijl de inkoop € 35,35 kostte.
  if (gekozenLengteMm != null ? gekozenLengteMm > PAKKET_MAX_LENGTE_MM : (art.handelslengteMm ?? 0) > PAKKET_MAX_LENGTE_MM) return "lengte";
  const lijst = ov?.eigenTransport;
  if (!lijst || lijst.length === 0) return null;
  if (lijst.includes(art.slug)) return "artikel";
  // Expliciet "Pakket" bij het artikel wint van een aangevinkte groep of subgroep.
  if (lijst.includes(pakketArtikel(art.slug))) return null;
  if (art.subgroep && lijst.includes(eigenTransportSubgroep(art.subgroep))) return "subgroep";
  if (art.groep && lijst.includes(eigenTransportGroep(art.groep))) return "groep";
  return null;
}

export function gaatMetEigenTransport(
  art: ArtikelVoorTransport | null | undefined,
  ov: Pick<CatalogOverrides, "eigenTransport"> | null | undefined,
  gekozenLengteMm?: number | null,
): boolean {
  return eigenTransportReden(art, ov, gekozenLengteMm) !== null;
}
